/*
 * jj-samples-drawer.js  (J&J, Oct 2026)
 * --------------------------------------------------------------------------
 * <samples-drawer> — slide-out drawer on PDPs listing the samples collection
 * (markup: snippets/jj-samples-drawer.liquid).
 *
 *  - Opens from any link to the samples collection inside #MainContent (e.g.
 *    the "Order Finish Samples" text block) or any [data-samples-drawer-open]
 *    element. Header/footer menu links are left alone.
 *    Cmd/Ctrl/Shift/middle-click still open the collection normally.
 *  - Customers tick one or more samples and add them in a single
 *    /cart/add.js request.
 *  - On success: cart count + cart drawer contents refresh in the background
 *    (FoxTheme pubsub, same path Hyper's product form uses), a confirmation
 *    message shows, and the drawer closes back to the PDP. The cart drawer is
 *    NOT opened and there is no redirect, whatever the theme's cart type is.
 *  - Samples already in the cart show "In cart" and can't be added twice;
 *    this stays in sync with cart changes made elsewhere (pubsub + a /cart.js
 *    refresh each time the drawer opens).
 * --------------------------------------------------------------------------
 */
(function () {
  if (!window.customElements || customElements.get('samples-drawer')) return;
  if (typeof DrawerComponent === 'undefined') return;

  class SamplesDrawer extends DrawerComponent {
    constructor() {
      super();
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.closeTimer = null;
      this.busy = false;
    }

    connectedCallback() {
      super.connectedCallback();

      this.collectionPath = (this.dataset.collectionPath || '/collections/samples').replace(/\/$/, '');
      this.items = Array.from(this.querySelectorAll('[data-sample-item]'));
      this.addButton = this.querySelector('[data-samples-add]');
      this.addLabel = this.querySelector('[data-samples-add-label]');
      this.clearButton = this.querySelector('[data-samples-clear]');
      this.messageEl = this.querySelector('[data-samples-message]');

      const { signal } = this.abortController;

      document.addEventListener('click', this.onDocumentClick, { signal });
      this.addEventListener('change', (event) => {
        if (!event.target.matches('[data-sample-checkbox]')) return;
        this.clearError();
        this.onSelectionChange();
      }, { signal });
      this.addButton && this.addButton.addEventListener('click', this.onAdd.bind(this), { signal });
      this.clearButton && this.clearButton.addEventListener('click', () => this.clearSelection(), { signal });
      this.querySelectorAll('[data-samples-select-all]').forEach((btn) => {
        btn.addEventListener('click', this.onSelectAll.bind(this), { signal });
      });

      if (window.FoxTheme && FoxTheme.pubsub) {
        this.cartUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.cart && Array.isArray(event.cart.items)) this.syncInCart(event.cart.items);
        });
      }

      this.onSelectionChange();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.cartUnsubscriber && this.cartUnsubscriber();
      clearTimeout(this.closeTimer);
    }

    /* ---------- opening ---------- */

    isTrigger(el) {
      if (!el || el.closest('[data-samples-ignore]')) return false;
      if (el.matches('[data-samples-drawer-open]')) return true;
      if (el.tagName !== 'A' || !el.getAttribute('href')) return false;
      // Only links in the page content (PDP blocks). Header / footer menu links
      // to the samples collection keep navigating normally.
      if (!el.closest('#MainContent')) return false;
      let url;
      try {
        url = new URL(el.getAttribute('href'), window.location.origin);
      } catch (e) {
        return false;
      }
      if (url.origin !== window.location.origin) return false;
      // Match /collections/samples, with or without a locale prefix or trailing slash.
      const path = url.pathname.replace(/\/$/, '');
      return path === this.collectionPath || path.endsWith(this.collectionPath);
    }

    onDocumentClick(event) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const trigger = event.target.closest('a, [data-samples-drawer-open]');
      if (!trigger || this.contains(trigger) || !this.isTrigger(trigger)) return;

      event.preventDefault();
      this.show(trigger);
    }

    prepareToShow() {
      clearTimeout(this.closeTimer);
      this.setMessage('');
      this.refreshCart();
    }

    get focusElement() {
      return this.querySelector('[data-sample-checkbox]:not(:disabled)') || this.querySelector('.drawer__close-btn') || this;
    }

    async refreshCart() {
      try {
        const res = await fetch(`${FoxTheme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        const cart = await res.json();
        this.syncInCart(cart.items || []);
      } catch (e) {
        /* non-blocking */
      }
    }

    /* ---------- selection ---------- */

    get checkboxes() {
      return Array.from(this.querySelectorAll('[data-sample-checkbox]'));
    }

    get selectedIds() {
      return this.checkboxes.filter((cb) => cb.checked && !cb.disabled).map((cb) => Number(cb.value));
    }

    onSelectionChange() {
      this.items.forEach((item) => {
        const cb = item.querySelector('[data-sample-checkbox]');
        item.classList.toggle('is-selected', !!(cb && cb.checked && !cb.disabled));
      });

      const count = this.selectedIds.length;
      if (this.addLabel) {
        let label = this.dataset.labelAddZero || 'Select samples';
        if (count === 1) label = this.dataset.labelAddOne || 'Add 1 sample to cart';
        if (count > 1) label = (this.dataset.labelAddMany || 'Add [count] samples to cart').replace('[count]', count);
        this.addLabel.textContent = label;
      }
      if (this.addButton) this.addButton.setAttribute('aria-disabled', count === 0 || this.busy ? 'true' : 'false');
      if (this.clearButton) this.clearButton.hidden = count === 0;

      this.querySelectorAll('[data-samples-group]').forEach((group) => {
        const btn = group.querySelector('[data-samples-select-all] .reversed-link__text');
        const boxes = Array.from(group.querySelectorAll('[data-sample-checkbox]:not(:disabled)'));
        const allOn = boxes.length > 0 && boxes.every((cb) => cb.checked);
        if (btn) btn.textContent = allOn ? 'Deselect all' : 'Select all';
        const wrapper = group.querySelector('[data-samples-select-all]');
        if (wrapper) wrapper.hidden = boxes.length === 0;
      });

    }

    onSelectAll(event) {
      const group = event.currentTarget.closest('[data-samples-group]');
      if (!group) return;
      const boxes = Array.from(group.querySelectorAll('[data-sample-checkbox]:not(:disabled)'));
      const allOn = boxes.every((cb) => cb.checked);
      boxes.forEach((cb) => (cb.checked = !allOn));
      this.clearError();
      this.onSelectionChange();
    }

    clearError() {
      if (this.messageEl && this.messageEl.dataset.state === 'error') this.setMessage('');
    }

    clearSelection() {
      this.checkboxes.forEach((cb) => (cb.checked = false));
      this.onSelectionChange();
    }

    syncInCart(cartItems) {
      const inCart = new Set(cartItems.map((item) => Number(item.variant_id || item.id)));
      this.items.forEach((item) => {
        const id = Number(item.dataset.variantId);
        const available = item.dataset.available === 'true';
        const isInCart = inCart.has(id);
        const cb = item.querySelector('[data-sample-checkbox]');
        const status = item.querySelector('[data-sample-status]');

        item.classList.toggle('is-in-cart', isInCart);
        if (cb) {
          cb.disabled = isInCart || !available;
          if (cb.disabled) cb.checked = false;
        }
        if (status) status.textContent = isInCart ? 'In cart' : available ? '' : 'Sold out';
      });
      this.onSelectionChange();
    }

    /* ---------- add to cart ---------- */

    setMessage(text, state = '') {
      if (!this.messageEl) return;
      this.messageEl.textContent = text;
      this.messageEl.dataset.state = state;
      this.messageEl.hidden = !text;
    }

    setBusy(busy) {
      this.busy = busy;
      if (!this.addButton) return;
      this.addButton.classList.toggle('btn--loading', busy);
      this.addButton.setAttribute('aria-disabled', busy || this.selectedIds.length === 0 ? 'true' : 'false');
    }

    async onAdd() {
      const ids = this.selectedIds;
      if (this.busy || ids.length === 0) return;

      this.setBusy(true);
      this.setMessage('');

      // Let the cart drawer (if the theme uses one) ask for its section HTML so
      // its contents refresh in the background. It is never opened from here.
      const sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
      );

      const body = JSON.stringify({
        items: ids.map((id) => ({ id, quantity: 1 })),
        sections,
        sections_url: window.location.pathname,
      });

      try {
        const res = await fetch(`${FoxTheme.routes.cart_add_url}.js`, {
          ...FoxTheme.utils.fetchConfig('javascript'),
          body,
        });
        const parsed = await res.json();

        if (!res.ok || parsed.status) {
          this.setMessage(parsed.description || parsed.message || this.dataset.msgError, 'error');
          return;
        }

        const cartRes = await fetch(`${FoxTheme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
        const cart = await cartRes.json();
        cart.sections = parsed.sections;
        // Updates header cart count, cart drawer contents and this drawer's "In cart" states.
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });

        const msg =
          ids.length === 1
            ? this.dataset.msgAddedOne || 'Sample added to your cart.'
            : (this.dataset.msgAddedMany || '[count] samples added to your cart.').replace('[count]', ids.length);
        this.setMessage(msg, 'success');

        document.dispatchEvent(new CustomEvent('samples:added', { detail: { variantIds: ids, cart } }));

        const delay = parseInt(this.dataset.closeDelay, 10) || 1800;
        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(() => this.hide(), delay);
      } catch (error) {
        console.error('[samples-drawer]', error);
        this.setMessage(this.dataset.msgError, 'error');
      } finally {
        this.setBusy(false);
        this.onSelectionChange();
      }
    }
  }

  customElements.define('samples-drawer', SamplesDrawer);
})();
